import React, { useState, useEffect, useCallback } from 'react';
import stockReorderApi, { errorMessage } from '../../../api/stockReorderApi';
import { money, num } from '../reorderUtils';

/**
 * Saved reorder sheets.
 *
 * These are private per user by design on the Python side — two people with
 * access to the same branch still cannot see each other's saved sheets — so
 * there is deliberately no "whose sheet is this" column.
 *
 * Opening one hands it to the Reorder Sheet tab rather than rendering a second,
 * poorer copy of the same table: a saved sheet is the same sheet, read-only.
 */
const HistoryTab = ({ branchId, businessUnits, onBranchChange, onOpenSnapshot, onToast, viewingId }) => {
    const [snapshots, setSnapshots] = useState([]);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');

    const load = useCallback(async (buId) => {
        if (!buId) {
            setSnapshots([]);
            return;
        }
        setBusy(true);
        setError('');
        try {
            const data = await stockReorderApi.listHistory(buId);
            setSnapshots(data.snapshots || []);
        } catch (err) {
            setError(errorMessage(err, 'Could not load saved sheets'));
        } finally {
            setBusy(false);
        }
    }, []);

    useEffect(() => {
        load(branchId);
    }, [branchId, load]);

    const open = async (id) => {
        setBusy(true);
        setError('');
        try {
            const data = await stockReorderApi.getHistory(id, branchId);
            onOpenSnapshot(data, branchId);
        } catch (err) {
            setError(errorMessage(err, 'Could not open that sheet'));
        } finally {
            setBusy(false);
        }
    };

    const remove = async (id) => {
        if (!window.confirm('Delete this saved sheet? This cannot be undone.')) return;
        setBusy(true);
        setError('');
        try {
            await stockReorderApi.deleteHistory(id, branchId);
            onToast('Saved sheet deleted.');
            await load(branchId);
        } catch (err) {
            setError(errorMessage(err, 'Could not delete that sheet'));
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="sr-stack">
            <section className="sr-card">
                <h2 className="sr-card-title">Saved sheets</h2>
                <div className="sr-field">
                    <label htmlFor="sr-hist-bu">Branch</label>
                    <select
                        id="sr-hist-bu"
                        value={branchId}
                        onChange={(e) => onBranchChange(e.target.value)}
                    >
                        <option value="">Select a branch…</option>
                        {businessUnits.map((bu) => (
                            <option key={bu.id} value={bu.id}>
                                {bu.name}
                            </option>
                        ))}
                    </select>
                </div>

                {error && <div className="sr-alert sr-alert-error">{error}</div>}
                {busy && <div className="sr-empty">Working…</div>}

                {!busy && branchId && snapshots.length === 0 && !error && (
                    <div className="sr-empty">
                        Nothing saved for this branch yet. Generate a sheet and choose “Save sheet”.
                    </div>
                )}

                {snapshots.length > 0 && (
                    <div className="sr-table-wrap">
                        <table className="sr-table">
                            <thead>
                                <tr>
                                    <th>Saved</th>
                                    <th>Label</th>
                                    <th title="Movement / lead time / safety, in days">Params</th>
                                    <th className="sr-right">Items</th>
                                    <th className="sr-right">Need reorder</th>
                                    <th className="sr-right">Value</th>
                                    <th />
                                </tr>
                            </thead>
                            <tbody>
                                {snapshots.map((s) => (
                                    <tr key={s.id} className={s.id === viewingId ? 'sr-row-open' : ''}>
                                        <td className="sr-nowrap">
                                            {new Date(s.created_at).toLocaleString()}
                                        </td>
                                        <td>{s.label || <span className="sr-muted">—</span>}</td>
                                        <td className="sr-nowrap">
                                            {s.params?.movement_days} / {s.params?.lead_time_days} /{' '}
                                            {s.params?.safety_days}
                                        </td>
                                        <td className="sr-right">{num(s.summary?.total_items)}</td>
                                        <td className="sr-right">
                                            {num(s.summary?.items_needing_reorder)}
                                        </td>
                                        <td className="sr-right">
                                            {money(s.summary?.total_reorder_value)}
                                        </td>
                                        <td className="sr-right sr-nowrap">
                                            <button
                                                className="sr-btn sr-btn-sm"
                                                onClick={() => open(s.id)}
                                                disabled={busy}
                                            >
                                                Open
                                            </button>
                                            <button
                                                className="sr-btn sr-btn-sm sr-btn-danger"
                                                onClick={() => remove(s.id)}
                                                disabled={busy}
                                            >
                                                Delete
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
                <p className="sr-hint">
                    Opening a saved sheet shows it in the <strong>Reorder Sheet</strong> tab,
                    read-only, until you go back to live data.
                </p>
            </section>
        </div>
    );
};

export default HistoryTab;
